import { useState } from 'react';

interface AngleVideo {
  name: string;
  category: 'horizontal' | 'hover' | 'vertical';
  degree: number;
  description: string;
  youtubeId: string;
}

const angles: AngleVideo[] = [
  { name: 'ヨコ 10°', category: 'horizontal', degree: 10, description: '繊細な線画に', youtubeId: 'Gummv2plBrM' },
  { name: 'ヨコ 20°', category: 'horizontal', degree: 20, description: '長時間の作業に', youtubeId: 'X3RfOC8S9XU' },
  { name: 'ヨコ 25°', category: 'horizontal', degree: 25, description: 'バランスの良い角度', youtubeId: 'HvuERxe5-Oc' },
  { name: 'ヨコ 30°', category: 'horizontal', degree: 30, description: '全体を見渡す', youtubeId: 'cbKNC7SqZGA' },
  { name: 'ヨコ 35°', category: 'horizontal', degree: 35, description: '構図確認に', youtubeId: 'RksOSvgGCa4' },
  { name: 'ホバー 20°', category: 'hover', degree: 20, description: '浮かせて描く', youtubeId: 'eyygIu16Vvk' },
  { name: 'ホバー 65°', category: 'hover', degree: 65, description: '動画視聴に', youtubeId: 'j22W_6LIizE' },
  { name: 'タテ 45°', category: 'vertical', degree: 45, description: '読書・閲覧に', youtubeId: '4IqFnm1Nv_c' },
  { name: 'タテ 75°', category: 'vertical', degree: 75, description: 'ビデオ通話に', youtubeId: 'QamxgstGZCs' },
];

const categoryMeta: Record<
  'horizontal' | 'hover' | 'vertical',
  { label: string; color: string; bgLight: string }
> = {
  horizontal: {
    label: 'ヨコ',
    color: '#8B6BAE',
    bgLight: 'rgba(139,107,174,0.08)',
  },
  hover: {
    label: 'ホバー',
    color: '#A88BC8',
    bgLight: 'rgba(168,139,200,0.08)',
  },
  vertical: {
    label: 'タテ',
    color: '#6B4E8E',
    bgLight: 'rgba(107,78,142,0.08)',
  },
};

const categories = [
  {
    number: '01',
    title: '描くための横向き角度',
    orientation: '横向き',
    angles: '10° / 20° / 25°',
    description: '線を引く・塗る・書く',
    icon: 'ri-brush-line',
    color: '#8B6BAE',
    image: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/93ab59bd04effd9994aeab92548a4b58.png'
  },
  {
    number: '02',
    title: '見るための横向き角度',
    orientation: '横向き',
    angles: '30° / 35°',
    description: '構図確認・全体チェック',
    icon: 'ri-eye-line',
    color: '#6B8E8B',
    image: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/d342437ceaa2d80575272208c0d9742e.png'
  },
  {
    number: '03',
    title: '調べるための縦向き角度',
    orientation: '縦向き',
    angles: '45° / 75°',
    description: 'SNS・WEBサイトの閲覧',
    icon: 'ri-search-line',
    color: '#8B7355',
    image: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/b50a9eb1c8ccf99da08dc09d3a9107b2.png'
  },
  {
    number: '04',
    title: '観る・入力のためのフロート角度',
    orientation: 'ホバー',
    angles: '20° / 65°',
    description: 'PC的利用・動画の視聴',
    icon: 'ri-play-circle-line',
    color: '#7B8BAE',
    image: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/3aefa30baf6f33254126f3b29d2c9592.png'
  }
];

type AnglePictogramProps = {
  category: 'horizontal' | 'hover' | 'vertical';
  degree: number;
  color: string;
  isActive: boolean;
};

function AnglePictogram({
  category,
  degree,
  color,
  isActive,
}: AnglePictogramProps) {
  const barColor = isActive ? color : '#D0D0D0';
  const baseColor = isActive ? '#C0C0C0' : '#E0E0E0';
  const shadowColor = isActive ? 'rgba(139,107,174,0.15)' : 'transparent';

  if (category === 'hover') {
    return (
      <div className="relative w-full h-14 flex items-end justify-center">
        <div
          className="w-10 h-[3px] rounded-full origin-left transition-all duration-500"
          style={{
            backgroundColor: barColor,
            transform: `rotate(-${degree}deg) translateY(-6px)`,
          }}
        />
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2px] rounded-full"
          style={{ backgroundColor: baseColor }}
        />
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full blur-sm transition-colors duration-300"
          style={{ backgroundColor: shadowColor }}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full h-14 flex items-end justify-center">
      <div
        className="w-10 h-[3px] rounded-full origin-left transition-all duration-500"
        style={{
          backgroundColor: barColor,
          transform: `rotate(-${degree}deg)`,
        }}
      />
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2px] rounded-full"
        style={{ backgroundColor: baseColor }}
      />
    </div>
  );
}

export default function AngleDesignSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeAngle = angles[activeIndex];

  return (
    <section id="angle-design" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー */}
        <div className="text-center mb-16">
          <span className="text-sm font-medium tracking-wider text-[#8B6BAE] uppercase mb-4 block">9 Angles × 4 Categories</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C2C2C] mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            <span className="text-[#8B6BAE]">9</span>つの角度、<span className="text-[#8B6BAE]">4</span>種類のカテゴリー設計
          </h2>
          <p className="text-lg text-[#6B6B6B] max-w-2xl mx-auto">
            用途に合わせた4つのカテゴリーで、あらゆるシーンに最適な角度を提供します。
          </p>
        </div>

        {/* ユーザーの悩み・共感セクション */}
        <div className="mb-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B6BAE]/5 rounded-full border border-[#8B6BAE]/10">
                <i className="ri-chat-quote-line text-[#8B6BAE] text-lg"></i>
                <span className="text-sm font-medium text-[#8B6BAE]">クリエイターの声</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group border border-[#F0F0F0] hover:border-[#8B6BAE]/20">
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/eb6131dbed5fa7c66e326630b3a54aa1.jpg"
                    alt="描きやすい角度を求めるクリエイター"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg flex-shrink-0">
                      <i className="ri-pencil-line text-[#8B6BAE] text-sm"></i>
                    </div>
                    <p className="text-sm text-white font-medium leading-relaxed" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
                      あと少し傾いてくれたら<br />描きやすいのに、、
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group border border-[#F0F0F0] hover:border-[#8B6BAE]/20">
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20young%20illustrator%20sitting%20outdoors%20in%20a%20park%20or%20garden%20with%20an%20iPad%2C%20enjoying%20fresh%20air%20and%20nature%2C%20bright%20sunny%20day%20with%20green%20plants%20and%20trees%20in%20background%2C%20peaceful%20and%20inspiring%20outdoor%20creative%20workspace%2C%20natural%20daylight%20photography&width=400&height=250&seq=creator-voice-outdoor-work&orientation=landscape"
                    alt="外で描くクリエイター"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg flex-shrink-0">
                      <i className="ri-sun-line text-[#8B6BAE] text-sm"></i>
                    </div>
                    <p className="text-sm text-white font-medium leading-relaxed" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
                      気分転換に外で描きたい
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group border border-[#F0F0F0] hover:border-[#8B6BAE]/20">
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20digital%20artist%20working%20in%20a%20small%20cramped%20cafe%20space%20with%20limited%20table%20area%2C%20iPad%20and%20coffee%20cup%20on%20tiny%20table%2C%20cozy%20but%20tight%20workspace%2C%20warm%20cafe%20interior%20lighting%2C%20showing%20the%20challenge%20of%20working%20in%20small%20spaces%2C%20overhead%20view%20of%20compact%20workspace&width=400&height=250&seq=creator-voice-cafe-space&orientation=landscape"
                    alt="カフェの小さいスペースで作業するクリエイター"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg flex-shrink-0">
                      <i className="ri-store-2-line text-[#8B6BAE] text-sm"></i>
                    </div>
                    <p className="text-sm text-white font-medium leading-relaxed" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
                      カフェの小さいスペースだと<br />作業がしにくい、、
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group border border-[#F0F0F0] hover:border-[#8B6BAE]/20">
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20portable%20creative%20workspace%20setup%20with%20iPad%20and%20accessories%20being%20packed%20into%20a%20stylish%20bag%2C%20professional%20desk%20environment%20transitioning%20to%20mobile%20setup%2C%20clean%20and%20organized%20workspace%20with%20modern%20aesthetic%2C%20showing%20the%20desire%20for%20portable%20productivity%2C%20soft%20studio%20lighting&width=400&height=250&seq=creator-voice-portable-desk&orientation=landscape"
                    alt="デスク環境を持ち運びたいクリエイター"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg flex-shrink-0">
                      <i className="ri-suitcase-line text-[#8B6BAE] text-sm"></i>
                    </div>
                    <p className="text-sm text-white font-medium leading-relaxed" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
                      デスクの環境を<br />気軽に持ち運びたい、、
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-10">
              <div className="inline-flex items-center gap-2 text-[#8B6BAE]">
                <i className="ri-arrow-down-line text-xl animate-bounce"></i>
              </div>
              <p className="text-base font-semibold text-[#2C2C2C] mt-3">
                そんな悩みを、<span className="text-[#8B6BAE]">4つのカテゴリー</span>で解決
              </p>
            </div>
          </div>
        </div>

        {/* 4つのカテゴリーカード */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {categories.map((category, index) => (
            <div
              key={index}
              className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group border border-[#F0F0F0] hover:border-[#8B6BAE]/20"
            >
              {/* イメージ画像 */}
              <div className="w-full h-56 overflow-hidden relative">
                <img
                  src={category.image}
                  alt={category.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent"></div>
                
                {/* 番号バッジ */}
                <div className="absolute top-4 right-4">
                  <span 
                    className="text-xs font-bold tracking-wider px-3 py-1.5 rounded-full text-white backdrop-blur-sm"
                    style={{ backgroundColor: `${category.color}90` }}
                  >
                    {category.orientation}
                  </span>
                </div>

                {/* オーバーレイコンテンツ */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg flex-shrink-0"
                    >
                      <i className={`${category.icon} text-lg`} style={{ color: category.color }}></i>
                    </div>
                    <div>
                      <h3 
                        className="text-sm font-bold text-white leading-snug mb-1"
                        style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                      >
                        {category.title}
                      </h3>
                      <p 
                        className="text-xl font-bold text-white"
                        style={{ fontFamily: 'Playfair Display, serif', textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                      >
                        {category.angles}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 下部の説明 */}
              <div className="px-5 py-4 flex items-center gap-2">
                <span className="text-2xl font-bold text-[#E8E8E8] group-hover:text-[#D5D5D5] transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>{category.number}</span>
                <div className="w-px h-5 bg-[#E8E8E8]"></div>
                <p className="text-xs text-[#6B6B6B]">{category.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 9つの角度セクション */}
        <div className="border-t border-[#E5E5E5] pt-16">
          {/* サブ見出し */}
          <div className="text-center mb-8">
            <p className="text-sm tracking-[0.3em] text-[#8B6BAE] uppercase mb-4">
              9 Angles Detail
            </p>
            <h3
              className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              角度を選んで動画で確認
            </h3>
            <p className="text-base text-[#9B9B9B] max-w-xl mx-auto">
              折り紙のように折るだけで、あなたの作業に最適な角度が見つかる
            </p>
          </div>

          {/* ピクトグラムグリッド */}
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-3 mb-10">
            {angles.map((angle, idx) => {
              const meta = categoryMeta[angle.category];
              const isActive = activeIndex === idx;
              return (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`
                    group relative rounded-2xl px-3 py-5 text-center transition-all duration-300 cursor-pointer border
                    ${isActive
                      ? 'bg-white shadow-lg border-[#8B6BAE]/15 scale-[1.03]'
                      : 'bg-[#FAFAFA] border-transparent hover:bg-white hover:shadow-md hover:border-[#F0F0F0]'}
                  `}
                >
                  {/* ピクトグラム */}
                  <AnglePictogram
                    category={angle.category}
                    degree={angle.degree}
                    color={meta.color}
                    isActive={isActive}
                  />

                  {/* 角度数値 */}
                  <p
                    className={`
                      text-2xl font-bold mt-3 mb-1 transition-colors duration-300
                      ${isActive ? 'text-[#8B6BAE]' : 'text-[#2C2C2C] group-hover:text-[#8B6BAE]'}
                    `}
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {angle.degree}°
                  </p>

                  {/* カテゴリラベル */}
                  <p
                    className="text-[10px] font-semibold tracking-wider mb-1.5 transition-colors duration-300"
                    style={{ color: isActive ? meta.color : '#9B9B9B' }}
                  >
                    {meta.label}
                  </p>

                  {/* 説明文 */}
                  <p
                    className={`
                      text-[10px] leading-tight transition-all duration-300
                      ${isActive ? 'text-[#4A4A4A] opacity-100' : 'text-[#9B9B9B] opacity-0 group-hover:opacity-100'}
                    `}
                  >
                    {angle.description}
                  </p>

                  {/* アクティブインジケーター */}
                  {isActive && (
                    <div
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-[3px] rounded-t-full"
                      style={{ backgroundColor: meta.color }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* 動画エリア */}
          <div className="w-full">
            <div
              className="relative w-full rounded-2xl overflow-hidden bg-[#F5F5F5] shadow-lg"
              style={{ aspectRatio: '16/9' }}
            >
              <iframe
                key={activeAngle.youtubeId}
                src={`https://www.youtube.com/embed/${activeAngle.youtubeId}?rel=0&modestbranding=1`}
                title={activeAngle.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                style={{ border: 'none' }}
              />
            </div>

            {/* 動画下の情報バー */}
            <div className="mt-6 flex items-center justify-center gap-4">
              <span
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium text-white whitespace-nowrap"
                style={{
                  backgroundColor:
                    categoryMeta[activeAngle.category].color,
                }}
              >
                {categoryMeta[activeAngle.category].label}
              </span>
              <h3
                className="text-2xl font-bold text-[#2C2C2C]"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {activeAngle.name}
              </h3>
              <span className="text-[#D0D0D0]">|</span>
              <p className="text-base text-[#9B9B9B]">
                {activeAngle.description}
              </p>
            </div>
          </div>

          {/* 下部メッセージ */}
          <div className="text-center mt-16">
            <div className="inline-flex items-center gap-4 px-8 py-4 bg-[#FAFAFA] rounded-full">
              <i className="ri-magic-line text-xl text-[#8B6BAE]"></i>
              <p className="text-base text-[#4A4A4A]">
                1枚のケースから、<span className="font-semibold text-[#8B6BAE]">9通り</span>
                の作業環境を
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
